import React from 'react';
import { Mail, Save, RotateCcw, AlertCircle } from 'lucide-react';
import Button from '@components/common/Button';
import { Input, Textarea } from '@components/common';
import { EmailTemplateDetail, TextBlockSchema } from '@app-types';
import styles from './EditorPanel.module.css';

export interface EditorPanelProps {
  selectedTemplate: EmailTemplateDetail | undefined;
  editedSubject: string;
  editedBlocks: Record<string, string>;
  isModified: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  isResetting: boolean;
  /** Placeholder-validation error surfaced under the subject field (proactive or backend 400). */
  subjectError?: string;
  /** False when the subject fails proactive placeholder validation — blocks Save. */
  canSave: boolean;
  onSubjectChange: (value: string) => void;
  onFieldChange: (key: string, value: string) => void;
  onSave: () => void;
  onResetClick: () => void;
}

const EditorPanel: React.FC<EditorPanelProps> = ({
  selectedTemplate,
  editedSubject,
  editedBlocks,
  isModified,
  hasChanges,
  isSaving,
  isResetting,
  subjectError,
  canSave,
  onSubjectChange,
  onFieldChange,
  onSave,
  onResetClick
}) => {
  if (!selectedTemplate) {
    return (
      <div className={styles.noSelection}>
        <Mail size={48} />
        <p>Select a template to edit</p>
      </div>
    );
  }

  return (
    <main className={styles.editorPanel} data-test-id="editor-panel">
      <div className={styles.editorHeader}>
        <h2 data-test-id="editor-title">{selectedTemplate.name}</h2>
        <p className={styles.templateDescription}>{selectedTemplate.description}</p>
        {isModified && (
          <div className={styles.modifiedBadge}>
            <AlertCircle size={14} />
            <span>Modified from default</span>
          </div>
        )}
      </div>

      {/* Subject + Text Block Fields */}
      <div className={styles.textBlocksForm} data-test-id="text-blocks-form">
        <div className={styles.formField} data-test-id="subject-field">
          <div className={styles.formFieldHeader}>
            <label htmlFor="email-subject">Subject</label>
          </div>
          <Input
            id="email-subject"
            type="text"
            value={editedSubject}
            onChange={(e) => onSubjectChange(e.target.value)}
            error={subjectError}
            dataTestId="subject"
            wrapperClassName={styles.formInputWrapper}
            maxLength={255}
          />
        </div>

        {selectedTemplate.text_blocks_schema.map((field: TextBlockSchema) => (
          <div key={field.key} className={styles.formField} data-test-id={`text-block-${field.key}`}>
            <div className={styles.formFieldHeader}>
              <label htmlFor={field.key}>{field.label}</label>
              {field.hint && <span className={styles.formFieldHint}>{field.hint}</span>}
            </div>
            {field.type === 'textarea' ? (
              <Textarea
                id={field.key}
                value={editedBlocks[field.key] ?? ''}
                onChange={(e) => onFieldChange(field.key, e.target.value)}
                rows={3}
                dataTestId={`text-block-${field.key}`}
              />
            ) : (
              <Input
                id={field.key}
                type="text"
                value={editedBlocks[field.key] ?? ''}
                onChange={(e) => onFieldChange(field.key, e.target.value)}
                dataTestId={`text-block-${field.key}`}
                wrapperClassName={styles.formInputWrapper}
              />
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className={styles.editorActions}>
        <Button
          variant="primary"
          onClick={onSave}
          disabled={!hasChanges || !canSave}
          loading={isSaving}
          loadingText="Saving..."
          data-test-id="save-button"
        >
          <Save size={16} />
          Save Changes
        </Button>
        <Button
          variant="secondary"
          onClick={onResetClick}
          disabled={!isModified || isResetting}
          data-test-id="reset-button"
        >
          <RotateCcw size={16} />
          Reset to Default
        </Button>
      </div>
    </main>
  );
};

export default EditorPanel;
